import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BaseComponent } from '../../../base.component';
import { Prompt } from '../../../../models/prompt.model';
import { BehaviorSubject, Observable, distinctUntilChanged, filter, map, takeUntil } from 'rxjs';
import { UserConfigsService } from '../../../../services/user-configs.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { currentPrompts } from '@store/ai-insights/prompts/prompts.selector';
import { Store } from '@ngrx/store';
import { promptsActions } from '@store/ai-insights/prompts/prompts.actions';

@Component({
    selector: 'lex-custom-ai-prompt-dialog',
    templateUrl: './custom-ai-prompt-dialog.component.html',
    styleUrls: ['./custom-ai-prompt-dialog.component.scss'],
    standalone: false
})
export class CustomAiPromptDialogComponent extends BaseComponent {

	public selectedPrompt: Prompt;
	public sharedPrompts: Prompt[] = [];
	public pinnedPrompts: Prompt[] = [];
	public recentPrompts: Prompt[] = [];

	public currentUserId: string = '';

	public promptForm: FormGroup;
	public searchBox: FormControl = new FormControl('');
	public showEditList: boolean = false;

	private globalPrompts$: Observable<Prompt[]> = this.store.select(currentPrompts());
	private localPromptsSubject: BehaviorSubject<Prompt[]> = new BehaviorSubject<Prompt[]>([]);
	private globalPrompts: Prompt[] = [];
	private localPrompts: Prompt[] = [];
	private isInit: boolean = true;
	private currentCaseId: string = '';

	constructor(
		private formBuilder: FormBuilder,
		private userConfigsService: UserConfigsService,
		private store: Store,
		@Inject(MAT_DIALOG_DATA) private data: Prompt) {
		super();
		if (data) {
			this.selectedPrompt = data;
		}
		this.currentUserId = this.userConfigsService?.currentConfig?.userId;
		this.currentCaseId = this.userConfigsService?.currentConfig?.currentCase.caseId;

		this.promptForm = this.formBuilder.group({
			title: ['', [Validators.required, Validators.maxLength(256)]],
			fullPrompt: [''],
			isSummaryPrompt: [false]
		});

		this.searchBox.valueChanges.pipe(
			distinctUntilChanged(),
			map((term: string) => {
				this.localPromptsSubject.next(this.globalPrompts);
				this.localPrompts = this.localPrompts.filter(p =>
					p.title.toLocaleLowerCase().includes(term.toLocaleLowerCase())
					|| p.fullPrompt.toLocaleLowerCase().includes(term.toLocaleLowerCase()));
				this.localPromptsSubject.next(this.localPrompts);
			}),
			takeUntil(this.destroy$)
		).subscribe();

		this.globalPrompts$.pipe(
			map((prompts) => {
				this.localPromptsSubject.next(prompts);
				this.globalPrompts = prompts;
			}),
			takeUntil(this.destroy$)
		).subscribe();

		this.localPromptsSubject.pipe(
			filter((prompts) => !!prompts),
			map((prompts: Prompt[]) => {
				this.localPrompts = prompts;
				this.sharedPrompts = prompts.filter(x => x.isShared);
				this.pinnedPrompts = prompts.filter(x => x.isPinned);
				this.recentPrompts = prompts.filter(x => !x.isPinned && !x.isShared);
				if (this.isInit) {
					if (!this.selectedPrompt?.promptId || !prompts.some(x => x.promptId === this.selectedPrompt?.promptId)) {
						if (this.sharedPrompts.length > 0)
							this.selectedPrompt = this.sharedPrompts[0];
						else if (this.pinnedPrompts.length > 0)
							this.selectedPrompt = this.pinnedPrompts[0];
						else if (this.recentPrompts.length > 0)
							this.selectedPrompt = this.recentPrompts[0];
					}
					const prompt = prompts.find(x => x.promptId === this.selectedPrompt?.promptId);
					this.initEditingPrompt(prompt);
					this.isInit = false;
				}
			}),
			takeUntil(this.destroy$)
		).subscribe();

	}

	public select(prompt: Prompt): void {
		this.selectedPrompt = prompt;
		this.initEditingPrompt(prompt);
	}

	public save(): void {
		if (!this.promptForm.valid)
			return;

		this.store.dispatch(promptsActions.patchPrompt({
			promptId: this.selectedPrompt.promptId,
			deltaPrompt: {
				title: this.promptForm.get('title').value,
				promptType: this.promptForm.get('isSummaryPrompt').value ? 1 : 0
			}
		}));

		this.promptForm.markAsPristine();
	}

	public sharePrompt(prompt: Prompt): void {
		this.store.dispatch(promptsActions.patchPrompt({
			promptId: prompt.promptId,
			deltaPrompt: { isShared: true, isPinned: false }
		}));

	}

	public pinPrompt(prompt: Prompt): void {
		this.store.dispatch(promptsActions.patchPrompt({
			promptId: prompt.promptId,
			deltaPrompt: { isShared: false, isPinned: true }
		}));
	}

	public unshareOrUnpinPrompt(prompt: Prompt): void {
		this.store.dispatch(promptsActions.patchPrompt({
			promptId: prompt.promptId,
			deltaPrompt: { isShared: false, isPinned: false }
		}));

	}

	public deletePrompt(prompt: Prompt): void {
		this.store.dispatch(promptsActions.removePrompt({ promptId: prompt.promptId }));
	}

	public disableSave(): boolean {
		if (this.promptForm.pristine)
			return true;
		if (this.promptForm.get('title').invalid)
			return true;
		return false;
	}

	public displaySave(): boolean {
		if (this.currentCaseId !== this.selectedPrompt.caseId)
			return false;
		return true;
	}

	private initEditingPrompt(prompt: Prompt): void {
		if (prompt) {
			this.promptForm.reset({
				title: { value: prompt.title, disabled: prompt.createdById !== this.currentUserId },
				fullPrompt: { value: prompt.fullPrompt, disabled: true },
				isSummaryPrompt: { value: prompt.promptType === 1, disabled: prompt.createdById !== this.currentUserId }
			});
		}
		else {
			this.promptForm.reset({
				title: { value: '', disabled: true },
				fullPrompt: { value: '', disabled: true },
				isSummaryPrompt: { value: false, disabled: true }
			});
		}
		this.promptForm.markAsPristine();
		this.promptForm.updateValueAndValidity();
	}
}
